import { type RefObject, useCallback, useRef, useState } from 'react';

import type { RecentSearchItem } from '@core/types/search';
import { MAX_RECENT_VIEWS, RECENT_VIEWS_KEY } from '@web/constants/app';
import { getFromStorage, saveInStorage } from '@web/utils/storage';

import { useSlabQuery } from '../../hooks/useSlabQuery';
import { ENTITY_TAB_ORDER } from '../EntitySearchInput/entityConfig';
import { type EntitySearchState, useEntitySearch } from '../EntitySearchInput/useEntitySearch';

export type GlobalSearchState = EntitySearchState & {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  inputRef: RefObject<HTMLInputElement>;
  getRecentItems: () => RecentSearchItem[];
  addRecentView: (item: RecentSearchItem) => void;
};

export const useGlobalSearch = (tenantId: string): GlobalSearchState => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const search = useEntitySearch({ entityTypes: ENTITY_TAB_ORDER });

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const { clearQuery } = search;
  const close = useCallback(() => {
    setIsOpen(false);
    clearQuery();
  }, [clearQuery]);

  const storageKey = `${RECENT_VIEWS_KEY}_${tenantId}`;

  const isRecentFallbackNeeded =
    isOpen && getFromStorage<RecentSearchItem[]>(storageKey, []).length === 0;

  const { data: recentQuotesData } = useSlabQuery(
    'GET quote summaries',
    {
      queryParams: {
        perPage: 3,
        page: 1,
      },
    },
    { enabled: isRecentFallbackNeeded },
  );

  const { data: recentProjectsData } = useSlabQuery(
    'GET project summaries',
    {
      queryParams: {
        perPage: 3,
        page: 1,
      },
    },
    { enabled: isRecentFallbackNeeded },
  );

  const getRecentViews = useCallback(
    (): RecentSearchItem[] => getFromStorage<RecentSearchItem[]>(storageKey, []),
    [storageKey],
  );

  const addRecentView = useCallback(
    (item: RecentSearchItem) => {
      const recent = getRecentViews().filter((r) => r.id !== item.id);
      recent.unshift(item);
      saveInStorage(storageKey, recent.slice(0, MAX_RECENT_VIEWS));
    },
    [getRecentViews, storageKey],
  );

  const getRecentItems = useCallback((): RecentSearchItem[] => {
    const stored = getRecentViews();
    if (stored.length > 0) return stored;
    const quoteItems: RecentSearchItem[] = (recentQuotesData?.items ?? []).map((quote) => ({
      id: quote.id,
      type: 'quotes' as const,
      name: quote.quoteNumber,
      url: `/quotes/${quote.id}`,
    }));
    const projectItems: RecentSearchItem[] = (recentProjectsData?.items ?? []).map((project) => ({
      id: project.id,
      type: 'projects' as const,
      name: project.name,
      url: `/projects/${project.id}`,
    }));
    return [...quoteItems, ...projectItems];
  }, [getRecentViews, recentQuotesData, recentProjectsData]);

  return {
    ...search,
    isOpen,
    open,
    close,
    inputRef,
    addRecentView,
    getRecentItems,
  };
};
